// src/app/components/reporting/components/insights-card.tsx
import { useState } from 'react';
import { ChevronDown, Sparkles, Trash2, CheckCircle2 } from 'lucide-react';
import type { SavedInsight } from '../types';

interface InsightCardProps {
  insight:        SavedInsight;
  isSelected:     boolean;
  onToggleSelect: (id: number) => void;
  onDelete:       (id: number) => void;
}

function timeAgo(iso: string): string {
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (h < 1)  return 'Just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function InsightCard({ insight, isSelected, onToggleSelect, onDelete }: InsightCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={() => setIsExpanded((e) => !e)}
        className="p-3 md:p-4 cursor-pointer transition-all"
        style={{
          backgroundColor: isSelected
            ? 'var(--alpha-teal-5)'
            : isExpanded
            ? 'var(--muted)'
            : 'transparent',
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-1.5 rounded-full shrink-0 mt-1"
            style={{ backgroundColor: insight.module_color, height: '40px' }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {insight.metric && (
                <span
                  style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: insight.module_color,
                  }}
                >
                  {insight.metric}
                </span>
              )}
              <span
                style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {insight.title}
              </span>
            </div>
            {!isExpanded && insight.subtitle && (
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {insight.subtitle}
              </p>
            )}
          </div>
          <ChevronDown
            className={`w-5 h-5 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            style={{ color: 'var(--muted-foreground)' }}
          />
        </div>
      </div>

      {isExpanded && (
        <div
          className="px-3 md:px-4 pb-4 space-y-3"
          style={{ backgroundColor: 'var(--muted)' }}
        >
          {/* Content — removed fixed pl-5 to allow full-width on mobile */}
          <div className="pl-3 md:pl-5">
            {insight.subtitle && (
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  lineHeight: '1.6',
                  marginBottom: '8px',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {insight.subtitle}
              </p>
            )}
            <div
              className="flex items-center gap-x-2 gap-y-1 flex-wrap"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}
            >
              <span
                className="px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: insight.module_color + '18',
                  color: insight.module_color,
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                {insight.module}
              </span>
              {insight.saved_from && <span>{insight.saved_from}</span>}
              <span>•</span>
              <span>{timeAgo(insight.created_at)}</span>
            </div>
          </div>

          {/* Action row */}
          <div className="pl-3 md:pl-5 flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onToggleSelect(insight.id); }}
              className="flex-1 py-2 rounded-lg transition-all"
              style={{
                backgroundColor: isSelected ? 'var(--alpha-teal)' : 'var(--background)',
                color: isSelected ? 'white' : 'var(--foreground)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                border: isSelected ? 'none' : '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              {isSelected
                ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Selected for Report
                  </span>
                )
                : 'Select for Report'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(insight.id); }}
              className="p-2 rounded-lg flex-shrink-0"
              title="Delete insight"
              style={{
                backgroundColor: 'var(--background)',
                border: '1px solid var(--border)',
                color: 'var(--muted-foreground)',
                cursor: 'pointer',
              }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div
            className="pl-3 md:pl-5 p-3 rounded-lg"
            style={{
              backgroundColor: 'var(--alpha-teal-5)',
              border: '1px solid var(--alpha-teal-12)',
            }}
          >
            <div className="flex items-start gap-2">
              <Sparkles
                className="w-4 h-4 shrink-0 mt-0.5"
                style={{ color: 'var(--alpha-teal)' }}
              />
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.5',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                <span
                  style={{
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                  }}
                >
                  AI Suggests:{' '}
                </span>
                Strong for sales decks · Shows tangible improvement · Good for {insight.module} narrative
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}